// =============================================================================
// VINUM - Main App Component
// =============================================================================

import React, { useEffect } from 'react';
import { AppRouter } from './router';
import { ToastContainer } from '@/components/ui/Toast';
import { ThemeProvider } from '@/components/providers';
import {
  useUIStore,
  useAuthStore,
  useCartStore,
  useFavoritesStore,
  initializeApiClient,
  setStorageAdapter,
} from '@vinum/shared';

// =============================================================================
// Web Storage Adapter
// =============================================================================

const webStorageAdapter = {
  getItem: async (key: string) => localStorage.getItem(key),
  setItem: async (key: string, value: string) => localStorage.setItem(key, value),
  removeItem: async (key: string) => localStorage.removeItem(key),
};

// =============================================================================
// App Component
// =============================================================================

export const App: React.FC = () => {
  const setWindowWidth = useUIStore((state) => state.setWindowWidth);
  const loadUser = useAuthStore((state) => state.loadUser);
  const loadCart = useCartStore((state) => state.loadCart);
  const loadFavorites = useFavoritesStore((state) => state.loadFavorites);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Initialize storage adapter and API client
  useEffect(() => {
    setStorageAdapter(webStorageAdapter);
    initializeApiClient();
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setWindowWidth]);

  // Load user data and cart on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadUser();
      loadCart();
      loadFavorites();
    }
  }, [isAuthenticated, loadUser, loadCart, loadFavorites]);

  return (
    <ThemeProvider>
      <AppRouter />
      <ToastContainer />
    </ThemeProvider>
  );
};

export default App;
